import React from "react";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { ROUTES_CONSTANTS } from "./constants";
import {
  NoAuthLayout,
  SuperAdminLayout,
  CompanyLayout,
  UserLayout,
} from "./layouts";
import {
  CompanyDetail,
  CompanyManagement,
  CompanyForm,
  ForgotPassword,
  Login,
  ResetPassword,
  Dashboard,
  CompanyDashboard,
  UsersList,
  UserForm,
  PermissionsList,
  PermissionForm,
  PermissionGroupsList,
  PermissionGroupForm,
} from "./pages";
import { getFromStorage } from "./utils";
import { AuthProvider, useAuth } from "./context";
import { Loader } from "./components";

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const Authentication = () => {
  const token = getFromStorage("accessToken");
  const { currentUser, isLoading } = useAuth();

  if (!token) {
    return <Navigate to={ROUTES_CONSTANTS.LOGIN} replace />;
  }

  if (isLoading || !currentUser) {
    return <Loader />;
  }

  if (currentUser.role_id === 0) {
    if (currentUser.company) {
      return <Navigate to={ROUTES_CONSTANTS.COMPANY_DETAIL} replace />;
    }
    return <Navigate to={ROUTES_CONSTANTS.COMPANY_MANAGEMENT} replace />;
  }

  if (currentUser.role_id === 1) {
    return <Navigate to={ROUTES_CONSTANTS.COMPANY_DASHBOARD} replace />;
  }

  return <Navigate to={ROUTES_CONSTANTS.DASHBOARD} replace />;
};

const Router = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SentryRoutes>
          <Route
            path={ROUTES_CONSTANTS.NO_ROUTE}
            element={<Authentication />}
          />
          <Route element={<SuperAdminLayout />}>
            <Route
              path={ROUTES_CONSTANTS.COMPANY_MANAGEMENT}
              element={<CompanyManagement />}
            />
            <Route
              path={ROUTES_CONSTANTS.REGISTER_COMPANY}
              element={<CompanyForm />}
            />
            <Route
              path={ROUTES_CONSTANTS.COMPANY_DETAIL}
              element={<CompanyDetail />}
            />
            <Route
              path={`${ROUTES_CONSTANTS.UPDATE_COMPANY}`}
              element={<CompanyForm />}
            />
          </Route>
          <Route element={<CompanyLayout />}>
            <Route
              path={ROUTES_CONSTANTS.COMPANY_DASHBOARD}
              element={<CompanyDashboard />}
            />
            <Route path={ROUTES_CONSTANTS.USERS} element={<UsersList />} />
            <Route
              path={ROUTES_CONSTANTS.INVITE_USER}
              element={<UserForm />}
            />
            <Route
              path={ROUTES_CONSTANTS.UPDATE_USER}
              element={<UserForm />}
            />
            <Route
              path={ROUTES_CONSTANTS.PERMISSIONS}
              element={<PermissionsList />}
            />
            <Route
              path={ROUTES_CONSTANTS.CREATE_PERMISSION}
              element={<PermissionForm />}
            />
            <Route
              path={ROUTES_CONSTANTS.UPDATE_PERMISSION}
              element={<PermissionForm />}
            />
            <Route
              path={ROUTES_CONSTANTS.PERMISSION_GROUPS}
              element={<PermissionGroupsList />}
            />
            <Route
              path={ROUTES_CONSTANTS.CREATE_PERMISSION_GROUP}
              element={<PermissionGroupForm />}
            />
            <Route
              path={ROUTES_CONSTANTS.UPDATE_PERMISSION_GROUP}
              element={<PermissionGroupForm />}
            />
          </Route>
          <Route element={<UserLayout />}>
            <Route
              path={ROUTES_CONSTANTS.DASHBOARD}
              element={<Dashboard />}
            />
          </Route>
          <Route element={<NoAuthLayout />}>
            <Route path={ROUTES_CONSTANTS.LOGIN} element={<Login />} />
            <Route
              path={ROUTES_CONSTANTS.FORGOT_PASSWORD}
              element={<ForgotPassword />}
            />
            <Route
              path={ROUTES_CONSTANTS.RESET_PASSWORD}
              element={<ResetPassword />}
            />
            <Route
              path={ROUTES_CONSTANTS.SET_PASSWORD}
              element={<ResetPassword />}
            />
          </Route>
        </SentryRoutes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Router;
